import React, { useState } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import useLoader from "../../../hooks/useLoader";
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Modal,
  RadioGroup,
  FormControlLabel,
} from "@material-ui/core";
import {
  PrimaryOutlinedCTAButton,
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Formik } from "formik";
import * as XLSX from "xlsx";
import {
  CustomFileUpload,
  CheckboxContainer,
  CustomCheckbox,
} from "../../common/FormInputs";
import { CheckBoxLabel } from "../../common/FormInputs";
import { useSnackbar } from "notistack";
import CaseService from "../../../services/CaseService";
import { CustomInputField } from "../../common/FormInputs";
import {
  Label,
  StyledAutocomplete,
} from "../ManagementTools/component/SplitAndUpload";
import { Popper } from "@material-ui/core";
import CustomSelect from "../NoticeGeneration/component/CustomSelect";
import { bulkDownloadValidationSchema } from "../../../helpers/validationSchema";

const useStyles = makeStyles(() => ({
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
  },
}));
//hi

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const MAX_FILES_ALLOWED = 1500;

const BulkDownload = ({ bulkDownload, setBulkDownload, menuItems }) => {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [modal, setModal] = useState(false);
  const [fileData, setFileData] = useState();

  const viewing_items = [
    {
      label: "Claimant documents",
      name: "claimant",
      value: "claimant",
    },
    {
      label: "Respondent documents",
      name: "respondent",
      value: "respondent",
    },
    {
      label: "Neutral documents",
      name: "other",
      value: "other",
    },
  ];

  const input_actions = [
    {
      label: "Pdf",
      name: "pdf",
      value: "pdf",
    },
    {
      label: "Docx",
      name: "docx",
      value: "docx",
    },
    {
      label: "Images",
      name: "images",
      value: "images",
    },
    {
      label: "All",
      name: "all",
      value: "all",
    },
  ];

  async function s3Upload(file, filedName, setFieldValue, fileName) {
    if (file) {
      try {
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const response = await readExcelFile(file, fileName, setFieldValue);
        setFieldValue(filedName, response);
        if (response?.length) {
          setFieldValue(fileName, file.name);
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  /**
   * @description Read xlsx file
   * @param {*} file
   * @returns
   */

  const readExcelFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    const headerRow = data[0];
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
      "CONTRACT NO",
      "Contract No.",
    ];
    const contractNumberIndex = headerRow.findIndex((header) =>
      possibleHeaders.includes(header.trim()),
    );

    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", {
        variant: "error",
      });
    }

    const contractNumbers = data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);

    return contractNumbers;
  };

  const MAX_FILES = 3500;
  const BATCH_SIZE = 10;

  const downloadFilesAsZip = async (data, fieldName) => {
    const zip = new JSZip();
    const totalFiles = Object.values(data).flat().length;

    if (totalFiles > MAX_FILES) {
      enqueueSnackbar(
        `You have selected ${totalFiles} files. Please download within the limit of ${MAX_FILES} files.`,
        {
          variant: "info",
        },
      );
      return;
    }

    let completedFiles = 0;
    const fileTracker = new Map();

    const downloadFile = async (url, folder) => {
      try {
        setLoader({
          state: true,
          message: `Downloaded: ${completedFiles}/${totalFiles}`,
        });
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch: ${url}`);

        const blob = await response.blob();
        let fileName = decodeURIComponent(url.split("/").pop());

        if (fileTracker.has(fileName)) {
          let count = fileTracker.get(fileName) + 1;
          fileTracker.set(fileName, count);
          const fileExt = fileName.includes(".")
            ? fileName.split(".").pop()
            : "";
          const baseName = fileName.replace(`.${fileExt}`, "");
          fileName = `${baseName}_${count}.${fileExt}`;
        } else {
          fileTracker.set(fileName, 1);
        }

        folder.file(fileName, blob);
        completedFiles++;
      } catch (error) {
        enqueueSnackbar(`"Download error:", ${error?.message}`, {
          variant: "error",
        });
      }
    };

    const downloadBatch = async (batch) => {
      await Promise.all(
        batch.map(({ url, folder }) => downloadFile(url, folder)),
      );
    };

    const allFiles = [];
    for (const [folderName, urls] of Object.entries(data)) {
      const folder = zip.folder(folderName);
      urls.forEach((url) => allFiles.push({ url, folder }));
    }

    for (let i = 0; i < allFiles.length; i += BATCH_SIZE) {
      const batch = allFiles.slice(i, i + BATCH_SIZE);
      await downloadBatch(batch);
    }

    setLoader({ state: true, message: "Generating ZIP file..." });
    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, `${fieldName}.zip`);
      setLoader({ state: false });
      setBulkDownload(false);
    });
  };

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "" });
      const stringParams = `?contractNos=${values?.contractNumber.join(
        ",",
      )}&ownerRole=${values?.ownerType.join(",")}&noticeCategory=${
        values?.noticeCategory
      }&docxToPDF=${
        values?.documentType.includes("all") ||
        values?.documentType.includes("docx")
          ? values?.docxToPDF
            ? 1
            : 0
          : 0
      }&partyId=${values.partyId}`;

      const response =
        await CaseService.getBulkDownloadDocumentDet(stringParams);
      const docxFiles = Object.fromEntries(
        Object.entries(response)
          .map(([key, urls]) => [
            key,
            urls.filter((url) => {
              const isDocx = url.endsWith(".docx");
              const isPdf = url.endsWith(".pdf");
              const isImage = /\.(jpg|jpeg|png|gif|png|tif|tiff)$/i.test(url);

              if (
                values.documentType.length === 0 ||
                values.documentType.includes("all") ||
                values?.docxToPDF
              ) {
                return true;
              }

              return (
                (values.documentType.includes("docx") && isDocx) ||
                (values.documentType.includes("pdf") && isPdf) ||
                (values.documentType.includes("images") && isImage)
              );
            }),
          ])
          .filter(([_, urls]) => urls.length > 0),
      );

      const totalCount = Object.values(docxFiles).flat().length;

      if (totalCount > MAX_FILES_ALLOWED) {
        enqueueSnackbar(
          `Too many files selected (${totalCount}). Maximum allowed is ${MAX_FILES_ALLOWED}. Please refine your selection.`,
          { variant: "error" },
        );
        setBulkDownload(false);
        return;
      }

      setFileData(docxFiles);
      if (Object.keys(docxFiles).length) {
        enqueueSnackbar("Successfully fetched all the files", {
          variant: "success",
        });
        setModal(true);
      } else {
        enqueueSnackbar("No files found", {
          variant: "error",
        });
        setBulkDownload(false);
      }
    } catch (err) {
      enqueueSnackbar(err?.message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <Modal
      open={bulkDownload}
      onClose={() => setBulkDownload(false)}
      disableRestoreFocus={true}
    >
      <DrawerContainer>
        <Container>
          <Paper elevation={3} style={{ padding: "16px" }}>
            <HeadingContainer>
              <Heading>Bulk Download</Heading>
            </HeadingContainer>
            <Formik
              initialValues={{
                contractNumber: "",
                fileName: "",
                ownerType: ["other"],
                documentType: ["pdf"],
                docxToPDF: false,
                noticeCategory: "",
                partyId: "",
              }}
              enableReinitialize
              validateOnBlur
              validateOnChange
              validationSchema={bulkDownloadValidationSchema}
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
                setFieldError,
                handleBlur,
                isValid,
              }) => (
                <>
                  {modal ? (
                    <>
                      <Grid
                        item
                        xs={12}
                        spacing={2}
                        style={{
                          marginTop: "16px",
                          padding: 16,
                        }}
                      >
                        <Description>
                          {`Are you sure you want to download all the contract number related files?`}
                        </Description>
                      </Grid>
                      <Flex>
                        <HalfWidth>
                          <PrimaryOutlinedCTAButton
                            style={{ padding: "10px 10px" }}
                            onClick={() => setModal(false)}
                          >
                            {"Back"}
                          </PrimaryOutlinedCTAButton>
                        </HalfWidth>

                        <HalfWidth>
                          <PrimaryCTAButton
                            style={{ padding: "10px 10px" }}
                            onClick={() =>
                              downloadFilesAsZip(fileData, values.fileName)
                            }
                          >
                            {"Download"}
                          </PrimaryCTAButton>
                        </HalfWidth>
                      </Flex>
                    </>
                  ) : (
                    <>
                      <Grid
                        item
                        xs={12}
                        spacing={2}
                        style={{ marginTop: "16px", padding: 16 }}
                      >
                        <RadioGroup
                          className={classes.radioGroup}
                          style={{
                            flexDirection: "row",
                            marginLeft: "10px",
                          }}
                          name={"ownerType"}
                        >
                          <CheckboxGridContainer>
                            {viewing_items.map((radio, key) => (
                              <FormControlLabel
                                {...{ key }}
                                classes={{ label: classes.formLabel }}
                                value={radio.value}
                                control={
                                  <CustomCheckbox
                                    size="medium"
                                    style={{ color: "#00838c" }}
                                    value={radio.value}
                                    checked={values.ownerType.includes(
                                      radio.value,
                                    )}
                                    onChange={(event) => {
                                      const { value, checked } = event.target;
                                      const newOwnerTypes = [
                                        ...values.ownerType,
                                      ];

                                      if (checked) {
                                        newOwnerTypes.push(value);
                                      } else {
                                        const index =
                                          newOwnerTypes.indexOf(value);
                                        if (index > -1) {
                                          newOwnerTypes.splice(index, 1);
                                        }
                                      }

                                      setFieldValue("ownerType", newOwnerTypes);
                                    }}
                                  />
                                }
                                label={radio.label}
                              />
                            ))}
                          </CheckboxGridContainer>
                        </RadioGroup>
                      </Grid>
                      <FormContainer>
                        <CustomSelect
                          name="partyId"
                          trigger={true}
                          getNoticeTemplates={() => {}}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          menuItems={menuItems}
                        />
                      </FormContainer>
                      <FormContainer style={{ marginTop: 5 }}>
                        <Label>Notice Category</Label>
                        <StyledAutocomplete
                          id="notice-category-autocomplete"
                          size="small"
                          options={menuItems}
                          groupBy={(option) => option.category}
                          getOptionLabel={(option) => option.name}
                          PopperComponent={CustomPopper}
                          renderInput={(params) => (
                            <div ref={params.InputProps.ref}>
                              <CustomInputField
                                {...params}
                                variant="outlined"
                                placeholder="Select notice category..."
                                style={{
                                  backgroundColor: "#ffffff",
                                  borderRadius: "10px",
                                }}
                              />
                            </div>
                          )}
                          renderGroup={(params) => (
                            <div style={{ padding: "8px 0" }}>
                              <div
                                style={{
                                  padding: "10px 16px",
                                  fontWeight: "700",
                                  fontSize: "11px",
                                  color: "#0f766e",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.8px",
                                  backgroundColor: "#f0fdf4",
                                  borderRadius: "6px",
                                  margin: "4px 8px",
                                }}
                              >
                                {params.group}
                              </div>
                              <div
                                style={{
                                  padding: "4px 0",
                                  color: COLORS.COLOR_DARK,
                                  fontFamily: theme.fonts.primaryFontSemiBold,
                                }}
                              >
                                {params.children}
                              </div>
                            </div>
                          )}
                          noOptionsText={
                            <div
                              style={{
                                color: COLORS.INPUT_LABEL,
                                fontFamily: "openSans-SemiBold, sans-serif",
                                fontSize: "14px",
                                padding: "8px",
                              }}
                            >
                              No document type found
                            </div>
                          }
                          onChange={(event, newValue) =>
                            setFieldValue(
                              "noticeCategory",
                              newValue?.name || "",
                            )
                          }
                        />
                      </FormContainer>

                      <FormContainer>
                        <Label>Upload xlsx file</Label>
                        <div style={{ marginBottom: 13 }}>
                          <CustomFileUpload
                            setFile={(file) =>
                              s3Upload(
                                file,
                                `contractNumber`,
                                setFieldValue,
                                `fileName`,
                              )
                            }
                            style={{
                              marginTop: 10,
                              width: "100%",
                            }}
                            accept=".xlsx"
                            file={{ name: values.fileName }}
                          />
                        </div>
                        <Grid
                          item
                          xs={4}
                          spacing={2}
                          style={{ marginTop: "16px", padding: 16 }}
                        >
                          <CheckboxGrid>
                            {input_actions.map((item, index) => (
                              <CheckboxContainer>
                                <CustomCheckbox
                                  name="documentType"
                                  checked={values.documentType.includes(
                                    item?.value,
                                  )}
                                  onChange={() => {
                                    if (
                                      values.documentType.includes(item.value)
                                    ) {
                                      setFieldValue(
                                        "documentType",
                                        values.documentType.filter(
                                          (arr) => arr !== item.value,
                                        ),
                                      );
                                    } else {
                                      setFieldValue("documentType", [
                                        ...values.documentType,
                                        item.value,
                                      ]);
                                    }
                                  }}
                                />
                                <CheckBoxLabel>{item.label}</CheckBoxLabel>
                              </CheckboxContainer>
                            ))}
                          </CheckboxGrid>
                        </Grid>
                        {(values?.documentType.includes("all") ||
                          values?.documentType.includes("docx")) && (
                          <Grid
                            item
                            xs={8}
                            spacing={2}
                            style={{ marginTop: "5px", paddingLeft: 16 }}
                          >
                            <CheckboxContainer>
                              <CustomCheckbox
                                name="docxToPDF"
                                checked={values.docxToPDF}
                                onChange={handleChange}
                              />
                              <CheckBoxLabel>Docx to pdf</CheckBoxLabel>
                            </CheckboxContainer>
                          </Grid>
                        )}
                        <Flex>
                          <HalfWidth>
                            <PrimaryOutlinedCloseCTAButton
                              style={{ padding: "10px 10px" }}
                              onClick={() => setBulkDownload(false)}
                            >
                              {"Cancel"}
                            </PrimaryOutlinedCloseCTAButton>
                          </HalfWidth>

                          <HalfWidth>
                            <PrimaryCTAButton
                              style={{ padding: "10px 10px" }}
                              disabled={!values.contractNumber.length}
                              onClick={handleSubmit}
                            >
                              {"Submit"}
                            </PrimaryCTAButton>
                          </HalfWidth>
                        </Flex>
                      </FormContainer>
                    </>
                  )}
                </>
              )}
            </Formik>
          </Paper>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default BulkDownload;

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const FormContainer = styled.div`
  padding-top: 5px;
  padding-left: 28px;
  padding-right: 54px;
  padding-bottom: 6px;
  & form {
    padding-top: 10px;
  }
`;

const Heading = styled(Typography)(() => ({
  backgroundColor: "#A5BFCC",
  color: "#fff",
  padding: "8px",
  fontFamily: theme.fonts.primaryFontBold,
  borderRadius: "5px",
  textAlign: "center",
}));

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 656px;
  }
`;

const HeadingContainer = styled.div`
  padding-right: 15px;
  padding-left: 15px;
  padding-top: 10px;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 47px;
  padding-left: 24px;
  padding-right: 24px;
`;

const HalfWidth = styled.div`
  width: 48%;
`;

const Description = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  line-height: 1.71;
  color: ${COLORS.COLOR_DARK};
  text-align: center;
  width: 100%;
  min-width: 100%;
  @media ${theme?.breakpoints?.sm_up} {
    font-size: 14px;
    min-width: 440px;
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  grid-column-gap: 10px;
`;

const CheckboxGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  grid-column-gap: 10px;
`;
